/**
 * Pagamento persistido (espelho da coleção `payments` no Firestore).
 */

import { z } from 'zod';

export const paymentStatusSchema = z.enum([
  'checkout_opened',
  'pending',
  'approved',
  'authorized',
  'in_process',
  'in_mediation',
  'rejected',
  'cancelled',
  'refunded',
  'charged_back',
]);

export type PaymentStatus = z.infer<typeof paymentStatusSchema>;

const billingMonthSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-01$/, 'Use YYYY-MM-01 (ex.: 2026-03-01).');

export const paymentSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().min(1),
  plan_id: z.string().uuid(),
  /** Id externo no gateway (ex.: Stripe `in_…`). Pode ser null antes da fatura existir. */
  gateway_payment_reference: z.string().min(1).nullable().optional(),
  /** Sessão Stripe Checkout (`cs_…`). */
  stripe_checkout_session_id: z.string().min(1).nullable().optional(),
  billing_month: billingMonthSchema,
  duration_months: z.number().int().min(1).max(120),
  amount_brl_cents: z.number().int().min(0),
  payment_type_id: z.string().nullable(),
  payment_method_id: z.string().nullable(),
  installments: z.number().int().min(1).max(24).default(1),
  status: paymentStatusSchema,
  status_detail: z.string().nullable(),
  currency_id: z.string().min(1).default('BRL'),
  external_reference: z.string().min(1).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type Payment = z.infer<typeof paymentSchema>;

export class PaymentRef {
  static readonly collection = 'payments';
  static docPath(id: string): string {
    return `${this.collection}/${id}`;
  }
}

/** Rótulo curto para listas (Minha conta). */
export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  checkout_opened: 'Checkout aberto',
  pending: 'Pendente',
  approved: 'Paga',
  authorized: 'Autorizada',
  in_process: 'Em processamento',
  in_mediation: 'Em mediação',
  rejected: 'Recusada',
  cancelled: 'Cancelada',
  refunded: 'Estornada',
  charged_back: 'Contestada',
};

/**
 * Normaliza campos legados ao ler Firestore/cache.
 */
export function normalizePaymentFields(
  id: string,
  raw: Record<string, unknown>
): Record<string, unknown> {
  const legacyNumericKey = "mercado_pago_payment_id" as const;
  const legacy = raw[legacyNumericKey];
  const existing = raw.gateway_payment_reference;
  let gateway_payment_reference: string | null | undefined;
  if (typeof existing === 'string' && existing.trim().length > 0) {
    gateway_payment_reference = existing.trim();
  } else if (
    typeof legacy === 'number' &&
    Number.isFinite(legacy) &&
    legacy > 0
  ) {
    gateway_payment_reference = String(legacy);
  } else if (typeof legacy === 'string' && legacy.trim().length > 0) {
    gateway_payment_reference = legacy.trim();
  } else {
    gateway_payment_reference = null;
  }
  const rawCs = raw.stripe_checkout_session_id;
  let stripe_checkout_session_id: string | null | undefined;
  if (typeof rawCs === 'string' && rawCs.trim().length > 0) {
    stripe_checkout_session_id = rawCs.trim();
  } else if (rawCs === null) {
    stripe_checkout_session_id = null;
  } else {
    stripe_checkout_session_id = undefined;
  }
  const { [legacyNumericKey]: _drop, ...rest } = raw as Record<string, unknown>;
  return {
    ...rest,
    id,
    gateway_payment_reference,
    stripe_checkout_session_id,
  };
}
